import type { ColumnDef } from '@tanstack/react-table'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { Switch } from '@/shared/components/ui/switch'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import { Eye, Edit, MoreVertical, Trash2 } from 'lucide-react'
import type { GroupExtensionListItem } from '@/modules/users/types/group'
import { DataTable } from '@/shared/components/ui/data-table'
import { formatDistanceToNow } from 'date-fns'
import { es } from 'date-fns/locale'

interface GroupsTableProps {
  groups?: GroupExtensionListItem[]
  onView?: (id: number) => void
  onEdit?: (id: number) => void
  onToggleLaboratorio?: (id: number) => void
  onTogglePermiso?: (id: number) => void
  onToggleCliente?: (id: number) => void
  onDelete?: (id: number) => void
  hideLaboratorioToggle?: boolean
  showClienteColumn?: boolean
}

export function GroupsTable({
  groups = [],
  onView,
  onEdit,
  onToggleLaboratorio,
  onTogglePermiso,
  onToggleCliente,
  onDelete,
  hideLaboratorioToggle = false,
  showClienteColumn = false,
}: GroupsTableProps) {
  const allColumns: ColumnDef<GroupExtensionListItem>[] = [
    {
      accessorKey: 'grupo_nombre',
      header: 'Nombre del Grupo',
      enableColumnFilter: true,
      cell: ({ row }) => {
        return <div className="font-semibold text-gray-900 dark:text-white">{row.getValue('grupo_nombre')}</div>
      },
    },
    {
      accessorKey: 'color_nombre',
      header: 'Color',
      cell: ({ row }) => {
        const nombre = row.original.color_nombre
        const tono = row.original.color_tono || '500'
        if (!nombre) return <span className="text-xs text-muted-foreground">—</span>
        return (
          <div className="flex items-center gap-2">
            <div
              className={`h-5 w-5 rounded-full border border-gray-300 dark:border-gray-600 shrink-0 bg-${nombre}-${tono}`}
            />
            <span className="text-xs text-muted-foreground font-mono">{nombre}-{tono}</span>
          </div>
        )
      },
    },
    {
      accessorKey: 'laboratorio',
      header: 'Laboratorio',
      cell: ({ row }) => {
        const isLaboratorio = row.getValue('laboratorio') as boolean
        const groupId = row.original.id

        return (
          <div className="flex items-center gap-2">
            <Switch
              checked={isLaboratorio}
              onCheckedChange={() => onToggleLaboratorio?.(groupId)}
              disabled={!onToggleLaboratorio}
              className="data-[state=checked]:bg-teal-500"
            />
            {isLaboratorio ? (
              <Badge className="badge-gradient hover:opacity-90">Sí</Badge>
            ) : (
              <Badge variant="outline" className="text-gray-400 border-gray-400">No</Badge>
            )}
          </div>
        )
      },
    },
    {
      accessorKey: 'permiso',
      header: 'Permiso Especial',
      cell: ({ row }) => {
        const hasPermiso = row.getValue('permiso') as boolean
        const groupId = row.original.id

        return (
          <div className="flex items-center gap-2">
            <Switch
              checked={hasPermiso}
              onCheckedChange={() => onTogglePermiso?.(groupId)}
              disabled={!onTogglePermiso}
              className="data-[state=checked]:bg-teal-500"
            />
            {hasPermiso ? (
              <Badge className="badge-gradient hover:opacity-90">Sí</Badge>
            ) : (
              <Badge variant="outline" className="text-gray-400 border-gray-400">No</Badge>
            )}
          </div>
        )
      },
    },
    {
      accessorKey: 'cliente',
      header: 'Cliente Activo',
      cell: ({ row }) => {
        const isCliente = row.getValue('cliente') as boolean
        const groupId = row.original.id

        return (
          <div className="flex items-center gap-2">
            <Switch
              checked={isCliente}
              onCheckedChange={() => onToggleCliente?.(groupId)}
              disabled={!onToggleCliente}
              className="data-[state=checked]:bg-teal-500"
            />
            {isCliente ? (
              <Badge className="badge-gradient hover:opacity-90">Sí</Badge>
            ) : (
              <Badge variant="outline" className="text-gray-400 border-gray-400">No</Badge>
            )}
          </div>
        )
      },
    },
    {
      accessorKey: 'fecha_creacion',
      header: 'Fecha de Creación',
      cell: ({ row }) => {
        const fecha = row.getValue('fecha_creacion') as string
        try {
          const distance = formatDistanceToNow(new Date(fecha), {
            addSuffix: true,
            locale: es,
          })
          return <span className="text-sm">{distance}</span>
        } catch {
          return <span className="text-sm text-muted-foreground">-</span>
        }
      },
    },
    {
      id: 'acciones',
      header: () => <div className="text-right">Acciones</div>,
      cell: ({ row }) => {
        const group = row.original
        return (
          <div className="flex justify-end">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="ghost" className="h-8 w-8 p-0">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Acciones</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {onView && (
                  <DropdownMenuItem onClick={() => onView(group.id)}>
                    <Eye className="mr-2 h-4 w-4" />
                    Ver detalles
                  </DropdownMenuItem>
                )}
                {onEdit && (
                  <DropdownMenuItem onClick={() => onEdit(group.id)}>
                    <Edit className="mr-2 h-4 w-4" />
                    Editar
                  </DropdownMenuItem>
                )}
                {onDelete && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => onDelete(group.id)}
                      className="text-destructive focus:text-destructive"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Eliminar
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )
      },
    },
  ]

  // Filtrar columnas según contexto
  const columns = allColumns.filter(col => {
    if (!('accessorKey' in col)) return true
    if (col.accessorKey === 'color_nombre' && !hideLaboratorioToggle) return false
    if (col.accessorKey === 'laboratorio' && hideLaboratorioToggle) return false
    if (col.accessorKey === 'cliente' && !showClienteColumn) return false
    if (col.accessorKey === 'permiso' && showClienteColumn) return false
    return true
  })

  return (
    <DataTable
      columns={columns}
      data={groups}
      defaultPageSize={25}
      pageSizeOptions={[10, 25, 50, 100]}
    />
  )
}
